/*
 * Copyright 2026, Corning Optical Communications
 * All Rights Reserved.
 */
import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import dayjs, { Dayjs } from 'dayjs';

export type TimeRange = 'lastWeek' | 'lastMonth' | 'all' | 'custom';

interface PerformanceFilters {
  timeRange: TimeRange;
  truncate: string;
  startDate: Dayjs | null;
  endDate: Dayjs | null;
  setTimeRange: (range: TimeRange) => void;
  setTruncate: (truncate: string) => void;
  setStartDate: (date: Dayjs | null) => void;
  setEndDate: (date: Dayjs | null) => void;
  // Auto-refresh state (always enabled, only interval is configurable)
  refreshInterval: number; // milliseconds
  refreshTrigger: number; // Counter to trigger refresh
  setRefreshInterval: (interval: number) => void;
  incrementRefreshTrigger: () => void;
}

const PerformanceContext = createContext<PerformanceFilters | undefined>(undefined);

export const usePerformanceFilters = () => {
  const context = useContext(PerformanceContext);
  if (!context) {
    throw new Error('usePerformanceFilters must be used within PerformanceProvider');
  }
  return context;
};

interface PerformanceProviderProps {
  children: ReactNode;
}

export const PerformanceProvider: React.FC<PerformanceProviderProps> = ({ children }) => {
  const [timeRange, setTimeRange] = useState<TimeRange>('lastWeek');
  const [truncate, setTruncate] = useState<string>('day');
  const [startDate, setStartDate] = useState<Dayjs | null>(null);
  const [endDate, setEndDate] = useState<Dayjs | null>(null);

  // Auto-refresh state with localStorage persistence
  // Default: 5-minute interval, or 0 for "Never"
  const [refreshInterval, setRefreshInterval] = useState<number>(() => {
    const stored = localStorage.getItem('chart-refresh-interval');
    return stored ? parseInt(stored) : 5 * 60 * 1000; // Default: 5 minutes
  });

  const [refreshTrigger, setRefreshTrigger] = useState<number>(0);

  const incrementRefreshTrigger = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Persist refresh interval preference
  useEffect(() => {
    localStorage.setItem('chart-refresh-interval', refreshInterval.toString());
  }, [refreshInterval]);

  // Auto-refresh timer (only active when refreshInterval > 0)
  useEffect(() => {
    if (refreshInterval === 0) {
      const timestamp = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
      console.log(`⏹️ [${timestamp}] [PerformanceContext] Auto-refresh disabled (Never)`);
      return;
    }

    const startTimestamp = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
    console.log(`⏰ [${startTimestamp}] [PerformanceContext] Auto-refresh timer started:`, refreshInterval / 1000, 'seconds');
    
    const timer = setInterval(() => {
      const triggerTimestamp = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
      console.log(`🔄 [${triggerTimestamp}] [PerformanceContext] Auto-refresh triggered, incrementing refreshTrigger`);
      incrementRefreshTrigger();
    }, refreshInterval);

    return () => {
      const stopTimestamp = new Date().toLocaleTimeString('en-US', { hour12: false, hour: '2-digit', minute: '2-digit', second: '2-digit', fractionalSecondDigits: 3 });
      console.log(`⏹️ [${stopTimestamp}] [PerformanceContext] Auto-refresh timer stopped`);
      clearInterval(timer);
    };
  }, [refreshInterval]);

  return (
    <PerformanceContext.Provider
      value={{
        timeRange,
        truncate,
        startDate,
        endDate,
        setTimeRange,
        setTruncate,
        setStartDate,
        setEndDate,
        refreshInterval,
        refreshTrigger,
        setRefreshInterval,
        incrementRefreshTrigger,
      }}
    >
      {children}
    </PerformanceContext.Provider>
  );
};
